import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { NoteAnomalie, ResponseAnprData } from 'shell/models/person';
import { AnprVM, FiltersFormPerson, PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFormik } from 'formik';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { initialValuesPerson } from 'shell/constants/initial-values-search';
import { useNavigate, createSearchParams } from 'react-router-dom';
import { Typography, useEventCallback } from '@mui/material';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { validationSchemaFiltersPerson, headCells } from 'shell/constants/person-list';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ItemVM } from 'shell/view-models/item';
import FiltersPerson from './filters';
import { PersonVM } from 'shell/view-models/search-person';
import { Order } from 'shell/view-models/order';
import { useUserProfile } from 'shell/hooks/use-user-profile';
// import { openPdf } from 'shell/utils/open-pdf';
import { Anomaly } from 'front-office/view-models/common-certificate-form';
import {
  releaseIdCardResponseTransformerForSearch,
  ReleaseIdCardTransofrmSearchType,
} from 'front-office/utils/release-id-card-response-transformer';
import { ReleaseIdCard } from 'front-office/view-models/release-id-card';
import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { AnomalyResponse } from 'shell/models/anomaly-response';
import { ActionProps } from 'shell/view-models/actions-props';
import { noop } from 'shell/utils/noop';

interface RelaseIdCardPersonSearch {
  setAnomalieList: (anomalieArray: Array<Anomaly>) => void;
  setErrorModal: (value: boolean) => void;
  pathname?: string; // destination url
  action: (props: ActionProps) => JSX.Element;
  url?: string;
  addedTitle?: string;
  isNoteAnomalieModalClosed?: boolean;
  checkForError?: (
    response: ReleaseIdCardTransofrmSearchType,
    anomalies: Array<Anomaly>
  ) => boolean;
}

interface NoteAnomalieBackeEndResponse {
  anomalie: Array<NoteAnomalie>;
  messaggioErrore?: string;
}

const ReleaseIdCardPersonSearch = ({
  setAnomalieList,
  setErrorModal,
  pathname = '',
  action: Action,
  url = '/persona/ricerca-rilascio-cie',
  addedTitle,
  checkForError,
}: RelaseIdCardPersonSearch): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const navigate = useNavigate();

  const [error] = useState<string | undefined>();
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const personFilterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr, getAnprData] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    ReleaseIdCardTransofrmSearchType
  >(`${__PERSON_NEW_API_URL__}${url}`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => releaseIdCardResponseTransformerForSearch(response, userProfile),
  });

  const getAnprFunc = useEventCallback((filters: FiltersFormPerson) => {
    filterPersonForm.setFieldValue('page', 0);

    personFilterTable?.setValues({
      filters,
      subject: null,
    });

    const data = transformSearchPersonDataset({ ...filters, page: 0 });

    (
      getAnprData({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, ReleaseIdCardTransofrmSearchType>
    ).then((res) => {
      const castedResponse = (res as ReleaseIdCardTransofrmSearchType)
        ?.releaseIdCard as ReleaseIdCard;
      const soggettoCertificabile =
        castedResponse?.dati3002?.attributiSoggetto?.soggettoCertificabile;
      const idSchedaSoggetto = castedResponse?.dati3002?.codicePersonaSipo;

      const responseTranformed = (res as ReleaseIdCardTransofrmSearchType)
        ?.anprPersonData as AnprVM<PersonVM>;

      onVisibility(responseTranformed?.errors?.length > 0, 'error');
      onVisibility(responseTranformed?.warnings?.length > 0, 'warning');

      if (idSchedaSoggetto && soggettoCertificabile) {
        (
          getAnomalieList({
            url: `${idSchedaSoggetto}`,
            params: {
              codiceNonCertificabilita: soggettoCertificabile,
            },
          }) as ResponsePromise<HttpResponse<AnomalyResponse>, Array<Anomaly>>
        ).then((anomalieResponse) => {
          const castedResponseAnomalie = (anomalieResponse as Array<Anomaly>) || [];
          if (responseTranformed?.persons?.length > 0) {
            //calling the PermitExpired Error function

            let hasError = false;

            if (checkForError) {
              hasError = checkForError(
                res as ReleaseIdCardTransofrmSearchType,
                castedResponseAnomalie
              );
            }

            if (responseTranformed?.persons?.length > 1) {
              personFilterTable?.setValues({
                subject: null,
                filters: {
                  ...filters,
                  isPersonsListVisited: true,
                  isChildComponentVisited: false,
                },
              });
            }

            if (
              !hasError &&
              responseTranformed?.persons?.length === 1 &&
              castedResponseAnomalie.length === 0
            ) {
              personFilterTable?.setValues({
                subject: responseTranformed?.persons[0],
                filters: {
                  ...filters,
                  isPersonsListVisited: true,
                  isChildComponentVisited: false,
                },
              });

              navigate({
                pathname,
                search: `?${createSearchParams({
                  ricercaNazionale:
                    filters?.subjectData?.isNationalResearch?.toString() ||
                    filters?.commonSearch?.isNationalResearch?.toString() ||
                    'false',
                  codiceFiscale: responseTranformed?.persons[0].fiscalCode || '',
                  idSchedaSoggettoANPR: responseTranformed?.persons[0].anpr || '',
                  idFamigliaANPR: responseTranformed?.persons[0].idFamilyAnpr || '',
                })}`,
              });
              return;
            }
          }
          setErrorModal(true);

          setAnomalieList(castedResponseAnomalie);
        });
      }
    });
  });

  useEffect(() => {
    if (
      personFilterTable?.values?.filters?.isChildComponentVisited &&
      personFilterTable?.values?.filters?.isPersonsListVisited
    ) {
      //calling the fucntion to the get the anpr result
      getAnprFunc(personFilterTable?.values?.filters);
      filterPersonForm.setValues(personFilterTable?.values?.filters);
    } else {
      personFilterTable?.setValues({
        filters: {
          ...personFilterTable?.values?.filters,
          page: personFilterTable?.values?.filters?.page || 0,
          orders: personFilterTable?.values?.filters?.orders || {},
          rowsPerPage: personFilterTable?.values?.filters?.rowsPerPage || 10,
          isChildComponentVisited: false,
          isPersonsListVisited: false,
        },
        subject: null,
      });
    }
  }, []);

  const getAnomalieList = useHttpClient<HttpResponse<NoteAnomalieBackeEndResponse>, Array<Anomaly>>(
    `${__PERSON_NEW_API_URL__}/persona/note-anomalie/anagrafiche/`,
    {
      options: {
        errorCatching: false,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response: AxiosResponse<HttpResponse<NoteAnomalieBackeEndResponse>>) => {
        const res = response?.data?.payload?.anomalie;

        const data: Array<Anomaly> = res?.map((item) => ({
          code: item?.codiceAnomalia?.toString(),
          description: item?.descrizioneNota,
          cancelDate: item?.dataCancellazione
            ? DateTime.fromISO(item?.dataCancellazione).toFormat('dd/MM/yyyy')
            : '',
          flag: item?.flagBlocco,
          insertDate: item?.key?.dataAnnotazione
            ? DateTime.fromISO(item?.key?.dataAnnotazione).toFormat('dd/MM/yyyy')
            : '',
          key: item?.key,
        }));
        return data;
      },
    }
  );

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? undefined : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const onSubmit = useEventCallback((formValues: FiltersFormPerson) => {
    filterPersonForm.setFieldValue('page', 0);
    personFilterTable?.setValues({
      filters: formValues,
      subject: null,
    });
    //calling the fucntion to get  the anpr result
    getAnprFunc(formValues);
  });

  const filterPersonForm = useFormik<FiltersFormPerson>({
    initialValues: {
      ...initialValuesPerson.filters,
      subjectData: {
        ...initialValuesPerson.filters?.subjectData,
        name: '',
        surname: '',
        withoutName: false,
        withoutSurname: false,
        ...(personFilterTable?.values?.filters?.subjectData || {}),
      },
      searchByFiscalCode: {
        ...initialValuesPerson.filters?.searchByFiscalCode,
      },
      commonSearch: {
        ...initialValuesPerson?.filters?.commonSearch,
        municipalityOfResidence: {
          id: userProfile.datiLogin?.codiceIstat,
          description: userProfile.datiLogin?.nomeComune || '',
          province: userProfile.datiLogin?.siglaProvincia || '',
        },
      },
    },
    validationSchema: validationSchemaFiltersPerson,
    onSubmit,
    onReset: () => {
      setVisibility({ error: false, warning: false });
    },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const body: Array<PersonVM> = useMemo(() => {
    const formattedBody: Array<PersonVM> = (
      (((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)
        ?.persons as Array<PersonVM>) || []
    ).map((person) => {
      return {
        ...person,
        action: (
          <Action
            cell={person}
            filterPersonForm={filterPersonForm.values}
            // handlePermitError={handlePermitError}
          />
        ),
      };
    });

    return formattedBody;
  }, [anpr, Action, filterPersonForm.values]);

  const personsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      (
        ((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)
          ?.persons as Array<PersonVM>
      )?.length > 0
    ) {
      personsRef?.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [anpr]);

  /*
  useEffect(() => {
    // filterPersonForm.setValues(filterTable.values || initialValuesPerson);
  }, []);

  useEffect(() => {
    // return () => filterTable.setValues(filterPersonForm.values);
  }, [filterPersonForm.values]);
*/
  const onSort = useEventCallback((orders: Order) => {
    const newDataset = transformSearchPersonDataset({ ...filterPersonForm.values, orders });
    return (
      getAnprData({
        data: newDataset,
      }) as ResponsePromise<HttpResponseList<ResponseAnprData>, ReleaseIdCardTransofrmSearchType>
    ).then(() => {
      filterPersonForm.setFieldValue('orders', orders);
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    filterPersonForm.setFieldValue('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = {
      ...filterPersonForm.values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };

    filterPersonForm.setValues(newDataset);
  });

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (error) {
      errorToVisualize.title = 'È presente un errore';
      errorToVisualize.messages = [
        {
          id: 1,
          value: error,
        },
      ];
    }

    if (
      ((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)?.errors
        ?.length
    ) {
      errorToVisualize.title =
        ((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)
          ?.errors?.length > 1
          ? `Ci sono ${
              ((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)
                ?.errors?.length
            } errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = (
        (anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>
      )?.errors;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [error, visibility.error, anpr.data]);

  return (
    <Fragment>
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Ricerca Persona {addedTitle || ''}
        </Typography>
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          ((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)
            ?.warnings?.length > 1
            ? `Ci sono ${
                (
                  (anpr.data as ReleaseIdCardTransofrmSearchType)
                    ?.anprPersonData as AnprVM<PersonVM>
                )?.warnings?.length
              } warning`
            : `C'è 1 warning`
        }
        messages={
          ((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)
            ?.warnings
        }
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : ((anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData as AnprVM<PersonVM>)
                ?.warnings?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <FiltersPerson filterPersonForm={filterPersonForm} onSubmit={noop} />
      <div
        ref={personsRef}
        style={{
          marginTop: 10,
        }}
      >
        {(anpr.data as ReleaseIdCardTransofrmSearchType)?.anprPersonData ? (
          !((body || []).length > 0) || visibility.error === false ? (
            <Typography className="pmi-pt-3" color="secondary.main">
              Nessun elemento trovato con la ricerca effettuata.
            </Typography>
          ) : (
            <Table
              ariaLabel="Lista Persone"
              page={filterPersonForm.values.page || 0}
              rowsPerPage={filterPersonForm.values.rowsPerPage}
              body={body}
              count={body?.length || 0}
              head={headCells}
              onSort={onSort}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              orders={filterPersonForm.values.orders || {}}
              isNotLayoutFixed
              noSort
              hideButtons
            />
          )
        ) : undefined}
      </div>
    </Fragment>
  );
};

ReleaseIdCardPersonSearch.displayName = 'Person Search component';

const ReleaseIdCardSearch = memo(ReleaseIdCardPersonSearch);

export default ReleaseIdCardSearch;
